import type { EraId } from '../config/eras';
import type { PosterPalette, PosterPose } from '../ui/posterStyle';
import { type Face, LAP_CARD_CAR, LAP_CARD_FIELD, type LapCard, lapCardOps, type Measure } from './lapCardLayout';
import { PosterArt } from './posterArt';

/**
 * A shared lap's link-preview picture (1200 × 630), drawn in the game when the
 * lap is shared (the server can't draw) and uploaded with the link: the day's
 * print, a red "you've been challenged!" label, the time to beat in the
 * biggest lettering, whose lap it is, and the day's car on a low sun.
 */
export interface ShareCard {
  era: EraId;
  palette: PosterPalette;
  /** "you've been challenged!" */
  label: string;
  /** "32.827" */
  time: string;
  /** "camilo's lap" */
  whose: string;
  /** "pista di poggibianco · the high nose 1994" */
  where: string;
}

const W = 1200;
const H = 630;
const BORDER = 20;
const CREAM = '#F3E6C8';
const POSE: PosterPose = { name: 'threeQuarter', azimuth: 40, elevation: 13, fov: 22, fill: 0.86, steer: 0.18, lean: 0.03 };
const DISPLAY = "'League Gothic', 'Arial Narrow', sans-serif";
const TEXT = "'Barlow Condensed', 'Arial Narrow', sans-serif";

let art: PosterArt | null = null;

/** The picture as a JPEG, or null if this browser can't draw it. */
export async function drawShareCard(o: ShareCard): Promise<Blob | null> {
  try {
    await Promise.all([
      document.fonts.load(`400 100px 'League Gothic'`),
      document.fonts.load(`700 30px 'Barlow Condensed'`),
      document.fonts.load(`600 30px 'Barlow Condensed'`),
    ]);
  } catch {
    // Fall back to the system's condensed faces.
  }
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  if (!g) return null;
  const p = o.palette;

  // The paper, and the painted field inside it.
  g.fillStyle = CREAM;
  g.fillRect(0, 0, W, H);
  g.fillStyle = p.field;
  g.fillRect(BORDER, BORDER, W - 2 * BORDER, H - 2 * BORDER);
  g.save();
  g.beginPath();
  g.rect(BORDER, BORDER, W - 2 * BORDER, H - 2 * BORDER);
  g.clip();
  // The low sun, and the car in front of it.
  g.globalAlpha = 0.92;
  g.fillStyle = p.sun;
  g.beginPath();
  g.arc(890, 330, 250, 0, Math.PI * 2);
  g.fill();
  g.globalAlpha = 1;
  try {
    art ??= new PosterArt(true);
    art.setPose(POSE, -1);
    art.show(o.era);
    if (art.drawAt(680, 540)) g.drawImage(art.canvas, 540, 70, 680, 540);
  } catch {
    // No WebGL: the lettering alone still says it.
  }
  g.restore();

  const x = 68;
  // The red label, like the poster's race button.
  g.font = `700 30px ${TEXT}`;
  const label = o.label.toUpperCase();
  const lw = spacedWidth(g, label, 3.5);
  g.fillStyle = p.accent;
  g.fillRect(x, 62, lw + 36, 50);
  g.fillStyle = p.accentText;
  spaced(g, label, x + 18, 98, 3.5);

  // "beat", then the time as big as it fits.
  g.fillStyle = p.title;
  g.font = `400 112px ${DISPLAY}`;
  g.fillText('beat', x - 4, 232);
  g.font = `400 ${fit(g, o.time, 470, 250, DISPLAY)}px ${DISPLAY}`;
  g.fillText(o.time, x - 6, 440);

  // Whose lap, and where.
  g.fillStyle = p.ink;
  g.font = `400 ${fit(g, o.whose, 470, 64, DISPLAY)}px ${DISPLAY}`;
  g.fillText(o.whose, x, 512);
  g.fillStyle = p.text;
  g.font = `600 ${fit(g, o.where, 470, 28, TEXT)}px ${TEXT}`;
  g.fillText(o.where, x, 553);
  g.fillStyle = p.ink;
  g.font = `700 21px ${TEXT}`;
  spaced(g, 'GRAND PREMIO · WATCH THE LAP, THEN RACE IT', x, 592, 2.6);

  // A little lighter than print quality (about 40 KB): it's a chat preview.
  return new Promise((resolve) => c.toBlob((b) => resolve(b), 'image/jpeg', 0.72));
}

export type { LapCard } from './lapCardLayout';

const FACES: Record<Face, string> = { display: DISPLAY, text: TEXT };

/**
 * A best lap's picture as the game draws it (src/lapcard.ts, in Cloudflare's headless browser, when the server
 * can't draw it from its template): the layout in lapCardOps, the car drawn live. A JPEG of at most `maxBytes` (a
 * little softer if it has to be), or null.
 */
export async function drawLapCard(o: LapCard, maxBytes = Infinity): Promise<Blob | null> {
  try {
    await Promise.all([document.fonts.load(`400 100px 'League Gothic'`), document.fonts.load(`700 30px 'Barlow Condensed'`), document.fonts.load(`600 30px 'Barlow Condensed'`)]);
  } catch {
    // Fall back to the system's condensed faces.
  }
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  if (!g) return null;
  const measure: Measure = (text, face, weight, size) => {
    g.font = `${weight} ${size}px ${FACES[face]}`;
    return g.measureText(text).width;
  };
  let clipped = false;
  for (const step of lapCardOps(o, measure)) {
    const clip = 'clip' in step && step.clip === true;
    if (clip && !clipped) {
      g.save();
      g.beginPath();
      g.rect(LAP_CARD_FIELD.x, LAP_CARD_FIELD.y, LAP_CARD_FIELD.w, LAP_CARD_FIELD.h);
      g.clip();
      clipped = true;
    } else if (!clip && clipped) {
      g.restore();
      clipped = false;
    }
    if (step.op === 'rect') {
      if (step.fill) {
        g.fillStyle = step.fill;
        g.fillRect(step.x, step.y, step.w, step.h);
      }
      if (step.stroke) {
        g.strokeStyle = step.stroke;
        g.lineWidth = step.line ?? 1;
        g.strokeRect(step.x, step.y, step.w, step.h);
      }
    } else if (step.op === 'circle') {
      g.globalAlpha = step.alpha ?? 1;
      g.fillStyle = step.fill;
      g.beginPath();
      g.arc(step.x, step.y, step.r, 0, Math.PI * 2);
      g.fill();
      g.globalAlpha = 1;
      if (step.stroke) {
        g.strokeStyle = step.stroke;
        g.lineWidth = step.line ?? 1;
        g.stroke();
      }
    } else if (step.op === 'car') {
      try {
        art ??= new PosterArt(true);
        art.setPose(POSE, -1);
        art.show(o.era, o.gold === true);
        if (art.drawAt(LAP_CARD_CAR.w, LAP_CARD_CAR.h)) g.drawImage(art.canvas, LAP_CARD_CAR.x, LAP_CARD_CAR.y, LAP_CARD_CAR.w, LAP_CARD_CAR.h);
      } catch {
        // No WebGL: the lettering alone still says it.
      }
    } else {
      g.font = `${step.weight} ${step.size}px ${FACES[step.face]}`;
      g.fillStyle = step.fill;
      if (step.spacing) spaced(g, step.text, step.x, step.y, step.spacing);
      else g.fillText(step.text, step.x, step.y);
    }
  }
  if (clipped) g.restore();
  let jpeg: Blob | null = null;
  for (const quality of [0.8, 0.7, 0.6, 0.5]) {
    jpeg = await new Promise<Blob | null>((resolve) => c.toBlob((b) => resolve(b), 'image/jpeg', quality));
    if (!jpeg || jpeg.size <= maxBytes) return jpeg;
  }
  return null;
}

/**
 * The day's car as the lap card shows it, alone on a clear background, as a PNG (a data URL), or null without 3D:
 * the server's lap card template pastes it in (public/cards/cars/full, scripts/card-parts.mjs).
 */
export function lapCardCar(era: EraId, gold: boolean): string | null {
  art ??= new PosterArt(true);
  art.setPose(POSE, -1);
  art.show(era, gold);
  if (!art.drawAt(LAP_CARD_CAR.w, LAP_CARD_CAR.h)) return null;
  return art.canvas.toDataURL('image/png');
}

/** The largest size (up to `max`) at which the text fits `width`. */
function fit(g: CanvasRenderingContext2D, text: string, width: number, max: number, family: string): number {
  const weight = family === DISPLAY ? 400 : 600;
  g.font = `${weight} ${max}px ${family}`;
  const w = g.measureText(text).width;
  return w <= width ? max : Math.floor((max * width) / w);
}

/** Letter-spaced text, drawn a letter at a time (not every browser spaces canvas text). */
function spaced(g: CanvasRenderingContext2D, text: string, x: number, y: number, gap: number): void {
  let at = x;
  for (const ch of text) {
    g.fillText(ch, at, y);
    at += g.measureText(ch).width + gap;
  }
}

function spacedWidth(g: CanvasRenderingContext2D, text: string, gap: number): number {
  let w = 0;
  for (const ch of text) w += g.measureText(ch).width + gap;
  return w - gap;
}

/**
 * The day's car alone on a cream disc, as a square PNG (a data URL), or null without 3D: the ready-made pictures
 * beside a lap message sent as a plain card, when its picture couldn't be made (public/cards/cars, scripts/card-parts.mjs).
 */
export function carMedallion(era: EraId, gold: boolean, size = 320): string | null {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = CREAM;
  g.beginPath();
  g.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  g.fill();
  art ??= new PosterArt(true);
  art.setPose(POSE, -1);
  art.show(era, gold);
  const w = Math.round(size * 0.96);
  const h = Math.round((w * 540) / 680);
  if (!art.drawAt(w, h)) return null;
  g.drawImage(art.canvas, (size - w) / 2, (size - h) / 2 + size * 0.03, w, h);
  return c.toDataURL('image/png');
}
